import { useEffect, useState } from "react";
import { api, fmtUSD } from "../lib/api";
import { useCart } from "../lib/cart";
import { Droplet, Plus, X } from "lucide-react";

const BAC_SLUG = "amino-h2o-10ml";

export default function BacWaterUpsellModal({ open, onClose }) {
    const { items, addItem } = useCart();
    const [bac, setBac] = useState(null);
    const [adding, setAdding] = useState(false);

    useEffect(() => {
        if (!open || bac) return;
        api.get(`/products/${BAC_SLUG}`).then((r) => setBac(r.data)).catch(() => setBac(null));
    }, [open, bac]);

    if (!open) return null;
    const alreadyInCart = items.some((i) => i.slug === BAC_SLUG);

    const add = () => {
        if (!bac) return;
        setAdding(true);
        addItem({ id: bac.id, slug: bac.slug, name: bac.name, strength: bac.strength, price: bac.price, image: bac.image }, 1);
        setTimeout(() => { setAdding(false); onClose(); }, 300);
    };

    return (
        <div
            data-testid="bacwater-upsell-modal"
            className="fixed inset-0 z-[110] flex items-end sm:items-center justify-center p-0 sm:p-4 bg-black/60 backdrop-blur-sm animate-in fade-in duration-200"
            onClick={onClose}
        >
            <div
                className="bg-[var(--bg-carbon)] border border-[var(--border)] w-full sm:max-w-md sm:rounded-lg shadow-2xl animate-in slide-in-from-bottom-4 sm:zoom-in-95 duration-200"
                onClick={(e) => e.stopPropagation()}
            >
                <div className="flex items-center justify-between px-5 py-4 border-b border-[var(--border)]">
                    <div className="flex items-center gap-2">
                        <Droplet size={16} className="text-brand" />
                        <span className="data-label text-brand">DON'T FORGET</span>
                    </div>
                    <button data-testid="bacwater-close" onClick={onClose} className="btn btn-ghost p-1.5">
                        <X size={14} />
                    </button>
                </div>

                <div className="p-5">
                    <h3 className="font-serif text-2xl text-primary mb-2">Don't forget your Amino H2O.</h3>
                    <p className="text-secondary text-sm mb-5 leading-relaxed">
                        Every lyophilized peptide needs sterile diluent for proper reconstitution. Add a 10 mL vial of Amino H2O to your order in one click.
                    </p>

                    {bac && (
                        <div className="flex items-center gap-4 p-4 border border-[var(--border)] bg-[var(--bg)] mb-5">
                            <img src={bac.image} alt={bac.name} className="w-16 h-16 object-contain" />
                            <div className="flex-1">
                                <div className="font-mono text-sm text-primary">{bac.name}</div>
                                <div className="data-label text-[0.6rem]">{bac.strength} · 10 mL</div>
                                <div className="font-serif text-xl text-brand mt-1">{fmtUSD(bac.price)}</div>
                            </div>
                        </div>
                    )}

                    {alreadyInCart ? (
                        <button data-testid="bacwater-already" disabled className="btn w-full opacity-70">
                            Already in your cart ✓
                        </button>
                    ) : (
                        <button
                            data-testid="bacwater-add-btn"
                            onClick={add}
                            disabled={!bac || adding}
                            className="btn btn-gold w-full"
                        >
                            <Plus size={14} /> {adding ? "Adding…" : "Add Amino H2O to Cart"}
                        </button>
                    )}
                    <button data-testid="bacwater-skip" onClick={onClose} className="btn btn-ghost w-full mt-2 text-xs">
                        No thanks, continue
                    </button>
                </div>
            </div>
        </div>
    );
}

export { BAC_SLUG };
